import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  Columns3,
  Ellipsis,
  Pencil,
  SortDesc,
} from 'lucide-react';
import { useState } from 'react';

// Shared mock component for "complex" content
function ColumnReorderList() {
  return (
    <div className="space-y-2">
      <h4 className="mb-3 text-sm font-medium">Reorder Columns</h4>
      <div className="space-y-1">
        {['Name', 'Status', 'Priority', 'Assignee', 'Due Date'].map((col) => (
          <div
            key={col}
            className="bg-background hover:bg-accent flex cursor-move items-center gap-2 rounded border
              p-2"
          >
            <div className="flex-1 text-sm">{col}</div>
            <input type="checkbox" defaultChecked />
          </div>
        ))}
      </div>
    </div>
  );
}

function BulkEditForm() {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium">Bulk Edit</h4>
      <div className="space-y-2">
        <div>
          <label className="text-muted-foreground text-xs">Status</label>
          <select className="w-full rounded border p-1 text-sm">
            <option>In Progress</option>
            <option>Done</option>
            <option>Blocked</option>
          </select>
        </div>
        <div>
          <label className="text-muted-foreground text-xs">Assignee</label>
          <input
            type="text"
            placeholder="Enter name..."
            className="w-full rounded border p-1 text-sm"
          />
        </div>
        <Button size="sm" className="w-full">
          Apply to Selected
        </Button>
      </div>
    </div>
  );
}

function SortList() {
  return (
    <div className="space-y-2">
      <h4 className="mb-3 text-sm font-medium">Sort Order</h4>
      <div className="space-y-1">
        {['Priority', 'Due Date'].map((col) => (
          <div
            key={col}
            className="bg-background flex items-center gap-2 rounded border p-2"
          >
            <div className="flex-1 text-sm">{col}</div>
            <select className="rounded border p-1 text-xs">
              <option>Asc</option>
              <option>Desc</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// Unified State Machine Pattern
type ContentState = 'menu' | 'columns' | 'sort' | 'bulk';

function UnifiedStateMachineDemo() {
  const [isOpen, setIsOpen] = useState(false);
  const [contentState, setContentState] = useState<ContentState>('menu');

  const showMenu = contentState === 'menu';
  const showContent = contentState !== 'menu';

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // if (!open) {
    //   // Reset to menu when popover closes
    //   setContentState('menu');
    // }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon">
          <Ellipsis />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-80 p-1"
        onCloseAutoFocus={() => setContentState('menu')}
      >
        {showMenu && (
          <div className="space-y-0.5">
            <button
              onClick={() => setContentState('bulk')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <Pencil className="size-4" />
              <span className="flex-1 text-left">Bulk Edit</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
            <button
              onClick={() => setContentState('sort')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <SortDesc className="size-4" />
              <span className="flex-1 text-left">Sort</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
            <button
              onClick={() => setContentState('columns')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <Columns3 className="size-4" />
              <span className="flex-1 text-left">Columns</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
          </div>
        )}

        {showContent && (
          <div className="p-3">
            <button
              onClick={() => setContentState('menu')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs"
            >
              <ChevronLeft className="size-3" />
              Back
            </button>
            {contentState === 'columns' && <ColumnReorderList />}
            {contentState === 'sort' && <SortList />}
            {contentState === 'bulk' && <BulkEditForm />}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function PopoverDemo() {
  return (
    <div className="p-8">
      <h2 className="mb-2 text-2xl font-bold">Unified State Machine Pattern</h2>
      <p className="text-muted-foreground mb-8">
        A clean solution for transitioning from a menu to complex components
        within a single popover. This pattern uses a state machine to smoothly
        swap between the menu view and detail views, eliminating coordination
        issues and timing bugs.
      </p>

      <div className="space-y-8">
        <div className="flex items-start gap-8 rounded-lg border p-8">
          <div className="flex-1">
            <h3 className="mb-3 text-lg font-medium">How It Works</h3>
            <div className="text-muted-foreground space-y-3 text-sm">
              <p>
                <strong className="text-foreground">Single Popover:</strong> The
                menu and detail views share the same popover component, avoiding
                coordination between multiple UI elements.
              </p>
              <p>
                <strong className="text-foreground">Separated State:</strong>{' '}
                Open/closed state is managed independently from content state.
                Content states include{' '}
                <code className="bg-muted rounded px-1 py-0.5 text-xs">
                  menu
                </code>
                , and detail states like{' '}
                <code className="bg-muted rounded px-1 py-0.5 text-xs">
                  columns
                </code>
                ,{' '}
                <code className="bg-muted rounded px-1 py-0.5 text-xs">
                  sort
                </code>
                , and{' '}
                <code className="bg-muted rounded px-1 py-0.5 text-xs">
                  bulk
                </code>
                . Transitions are explicit and predictable.
              </p>
              <p>
                <strong className="text-foreground">Smooth Transitions:</strong>{' '}
                Content swaps within the same container, providing seamless
                navigation without gaps or flashes.
              </p>
              <p>
                <strong className="text-foreground">Navigation Cues:</strong>{' '}
                Chevrons indicate forward navigation to detail views, while a
                back button returns to the menu.
              </p>
            </div>

            <div className="bg-muted/50 mt-6 rounded-md p-4">
              <h4 className="mb-2 text-sm font-medium">Key Benefits</h4>
              <ul className="text-muted-foreground list-inside list-disc space-y-1 text-sm">
                <li>No timing issues or useEffect coordination</li>
                <li>Clear, predictable state management</li>
                <li>Easy to test and debug</li>
                <li>Better UX with instant transitions</li>
              </ul>
            </div>

            <div className="mt-4 rounded-md border border-blue-500/20 bg-blue-500/10 p-4">
              <h4 className="mb-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                💡 Animation Tip
              </h4>
              <p className="text-muted-foreground text-xs">
                Use{' '}
                <code className="bg-muted rounded px-1 py-0.5">
                  onCloseAutoFocus
                </code>{' '}
                to reset content state <strong>after</strong> the close
                animation completes. Resetting in{' '}
                <code className="bg-muted rounded px-1 py-0.5">
                  onOpenChange
                </code>{' '}
                causes content to switch during the fade-out, creating a visual
                jerk.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4">
            <div className="text-muted-foreground text-center text-sm">
              Click to test the interaction →
            </div>
            <UnifiedStateMachineDemo />
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg border p-6">
          <h3 className="mb-3 font-medium">Implementation Notes</h3>
          <div className="text-muted-foreground space-y-3 text-sm">
            <div>
              <p className="mb-2">
                This pattern replaces the dropdown + popover coordination
                approach with a single component. Instead of:
              </p>
              <ul className="ml-4 list-inside list-disc space-y-1">
                <li>Dropdown menu for actions</li>
                <li>
                  Hidden popover trigger that gets clicked programmatically
                </li>
                <li>useEffect to coordinate state between components</li>
                <li>Virtual anchors and timing dependencies</li>
              </ul>
            </div>

            <div>
              <p className="text-foreground font-medium">
                Key Implementation Details:
              </p>
              <ul className="mt-1 ml-4 list-inside list-disc space-y-1">
                <li>
                  <strong>Separate state:</strong>{' '}
                  <code className="bg-muted rounded px-1 py-0.5">isOpen</code>{' '}
                  for visibility,{' '}
                  <code className="bg-muted rounded px-1 py-0.5">
                    contentState
                  </code>{' '}
                  for content
                </li>
                <li>
                  <strong>Reset timing:</strong> Use{' '}
                  <code className="bg-muted rounded px-1 py-0.5">
                    onCloseAutoFocus
                  </code>{' '}
                  not{' '}
                  <code className="bg-muted rounded px-1 py-0.5">
                    onOpenChange
                  </code>{' '}
                  to avoid content flash during animation
                </li>
                <li>
                  <strong>Single popover:</strong> Conditional rendering based
                  on state, styled to match dropdown menu appearance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
